import React, {useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";
import {useSubheader} from "../../../_metronic/layout";
import {
	capitalize,
	getDefaultInsightByPreference,
	getFeaturesRecursively,
	ObjectIsEmpty,
	TimeStampToSeconds,
} from "../../../_metronic/_helpers";
import {sendDataLayer} from "../../util/tagmanager";
import {connectSearchBox, connectStateResults} from "react-instantsearch-core";

//actions:
import {mediaItemPageActions} from "../../redux/MediaItemPage/mediaItemRedux";
import {reviewActions} from "../../redux/Review/reviewRedux";
import {setHeaderImg, setLogo} from "../../redux/Layout/layoutSlice";
import {getLanguageId, loadUserLanguageParameter} from "../../redux_slices/userParametersSlice";

//components:
import MediaViewer from "./MediaViewer";
import BadgesBoard from "../Review/BadgesBoard";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import CarouselVideoPage from "./Carousel";

//logic:
import {
	checkIsConceptualSearch,
	filterAndUnpackHits,
	getAppearances,
	getMergedAndSortedHits,
} from "./MediaDashboardLogic";
import {loadParameter, loadUserParameter} from "../../redux_slices/contextSlice";

const MediaDashboardConnectedToSearchBox = connectStateResults(props => {
	const {allSearchResults, searching, page, match, currentRefinement, refine} = props;
	const videoId = match.params.videoId;
	const isMediaPage = page === "videos";
	const isReviewPage = page === "review";
	const playerId = "videoPlayer";
	const exactRefine = s => {
		s ? refine('"' + s.replaceAll('"', "") + '"') : refine(s);
	};

	const allHits = allSearchResults?.hits || [];
	const languageId = useSelector(loadUserLanguageParameter);
	const parsedHits = useMemo(() => parseHitsByLanguage(allHits));

	const videoHits = useMemo(() => {
		const filteredResultsById = parsedHits.filter(hit => `${hit?.video?.id}` === videoId);
		const packedResults = filterAndUnpackHits(filteredResultsById);
		return parsedHits.length && currentRefinement ? packedResults : [];
	}, [allHits, currentRefinement]);

	function parseHitsByLanguage (hits) {
        const results = hits.filter(obj => {
            return obj.language_id === languageId;
        });
        return results;
    }

	const subheader = useSubheader();

	//redux:
	//store:
	const {
		info = {},
		netGraphData = [],
		videoNetgraphConfig = {},
		videoNetgraphInsights = [],
		isLoadingVideoData = true,
		videoTerm = {},
		isConceptualSearch = false,
	} = useSelector(state => state.mediaItemPage);
	const {errorMessage = {}} = useSelector(state => state.error);
	const corporativeLogo = useSelector(state => loadParameter(state, "corporativeLogo"));
	const contextSelector = useSelector(state => state.context);
	const language = useSelector(state => loadUserParameter(state, "language"));

	//actions:
	const dispatch = useDispatch();

	//local state:
	const [videoTermSearch, setVideoTermSearch] = useState(videoTerm);
	const [showSuggestion, setShowSuggestion] = useState(false);
	const [insightsOptions, setInsightsOptions] = useState([""]);
	const [notFoundMessage, setNotFoundMessage] = useState("");
	const [timelineMatches, setTimeLineMatches] = useState([]);
	const [timeLineFeature, setTimeLineFeature] = useState([]);
	const [selectedInsights, setSelectedInsights] = useState([{key: null}]);

	const [timelineHighlight, setTimelineHighlight] = useState("");
	const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
	const [videoIsReady, setVideoIsReady] = useState(false);
	const [nodeAppearances, setNodeAppearances] = useState(null);
	const [isGettingAppearances, setIsGettingAppearances] = useState(false);

	const [isNetgraphSearch, setIsNetgraphSearch] = useState(false);
	const [oldInsights, setOldInsights] = useState("");

	const [clickedText, setClickedText] = useState();

	const [lastPromise, setLastPromise] = useState(null);

	const setTimeline = time => {
		let t = TimeStampToSeconds(time);

		setPlayerCurrentTime(null);
		setTimeout(() => {
			setPlayerCurrentTime(t);
		}, 1);

		setTimelineHighlight(time);
	};

	const resetTimeline = () => {
		setTimeLineFeature([]);
		setTimelineHighlight("");

		setTimeLineMatches([]);
	};

	const choosingSearchType = (name, id) => {
		const isNotSameSearchItem = name && name !== currentRefinement;
		if (id && isNotSameSearchItem) {
			const isConceptual = checkIsConceptualSearch(id);
			dispatch(mediaItemPageActions.setIsConceptualSearch(isConceptual));

			exactRefine(name);
		}
	};

	const getNodeAppearances = (node, term) =>
		getAppearances({
			node,
			videoIdUrl: videoId,
			setIsGettingAppearances,
			selectedInsights,
			setNodeAppearances,
			choosingSearchType,
			term,
		});

	const handleMatchClick = (startTime, text = null) => {
		if (startTime) {
			const videoTag = document.getElementById(playerId);
			if (videoTag) {
				setTimeline(startTime);
			}
		} else if (text) {
			let parsedText = text.replaceAll('<span class="text-highlight">', "");
			parsedText = parsedText.replaceAll("</span>", "");
			parsedText = parsedText.replaceAll(/(?![A-Za-z])./gim, ".?");
			parsedText = [...parsedText]?.map(c => c + ".?").join("");
			parsedText = parsedText.replaceAll(".....?????", ".?.?.?.?.?");
			parsedText = parsedText.replaceAll("....????", ".?.?.?.?");
			parsedText = parsedText.replaceAll("...???", ".?.?.?");
			parsedText = parsedText.replaceAll("..??", ".?.?");
			parsedText = parsedText.replaceAll(".?", "((?![A-Za-z]).)?");
			parsedText = parsedText.replaceAll(/(\(\(\?!\[A-Za-z\]\)\.\)\?){1,}/g, "((?![A-Za-z]).)*");
			parsedText = parsedText.replace(/.$/, "?");
			if (parsedText.startsWith("((?![A-Za-z]).)*"))
				parsedText = parsedText.replace("((?![A-Za-z]).)*", "((?![A-Za-z]).)?");
			const regex = new RegExp(parsedText, "gmi");
			setClickedText(regex.source);
		}
	};

	const onNetgraphNodeClick = node => {
		setIsNetgraphSearch(true);
		getNodeAppearances(node);
	};

	const getNodeFromId = (id, featureType) => {
		if (featureType === "keywords") featureType = "highlights";
		if (netGraphData?.length) {
			for (let i = 0; i < netGraphData.length; i++) {
				const masterNode = netGraphData[i];
				if (masterNode?.id === id) return masterNode;
				const slaveNodes = masterNode.features[featureType];
				for (let j = 0; j < slaveNodes?.length; j++) {
					const slaveNode = slaveNodes[j];
					if (slaveNode?.id === id) return slaveNode;
				}
			}
		}
		return null;
	};

	useEffect(() => {
        dispatch(getLanguageId(language))
    }, []);

	useEffect(() => {
		if (info?.id) {
			sendDataLayer(
				{
					event: "video_visited",
					from: document.referrer,
					video_id: info.id,
					video_name: info.title,
					video_duration: info.duration,
				},
				{context: contextSelector}
			);
			if (videoId) {
				dispatch(
					mediaItemPageActions.getVideoNetgraphData(
						videoId,
						page,
						(info?.type_id === 1 && language) || info?.language?.code || language || "en-US"
					)
				);
			}
		}
	}, [info, contextSelector]);

	useEffect(() => {
		if (videoId) {
			const CONFIG_TYPE = 5;
			if (page === "review") dispatch(reviewActions.getVideoInfo(videoId));
			else dispatch(mediaItemPageActions.getVideoInfo(videoId, language || "en-US"));
			dispatch(mediaItemPageActions.getVideoNetgraphConfig(CONFIG_TYPE));
			dispatch(mediaItemPageActions.getVideoNetgraphInsights(videoId, page));
			setVideoIsReady(true);

			if (page === "review") {
				dispatch(reviewActions.getPeopleDropdown());
			}
		}

		return () => {
			dispatch(mediaItemPageActions.clear());
			subheader.setTitle("");
			subheader.setSubTitle("");
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoId]);

	useEffect(() => {
		if (info?.type_id && videoTermSearch?.text) {
			const hasConfig = !ObjectIsEmpty(videoNetgraphConfig);
			const hasData = netGraphData?.length;
			setVideoTermSearch(videoTerm);
			let name = videoTermSearch?.text;
			if (hasData) {
				const nodeId = videoTermSearch.id || videoTermSearch.selectedNode?.id;
				const nodeFromMediaPage = getNodeFromId(nodeId, videoTermSearch.type);
				if (nodeFromMediaPage) name = nodeFromMediaPage?.name;
			}
			if (hasData && hasConfig && name && !nodeAppearances && !isGettingAppearances) {
				const isANodeSelected = videoTermSearch.id || videoTermSearch.selectedNode?.id;

				if (!isANodeSelected) {
					setNodeAppearances([]);
					choosingSearchType(name, videoTermSearch.type);
				} else {
					getNodeAppearances(videoTermSearch, name);
				}
			}
		}
	}, [netGraphData, videoNetgraphConfig, info, videoTerm, videoTermSearch]);

	useEffect(() => {
		if (!isLoadingVideoData) {
			if (info) {
				//setting page title:
				const newTitle = info.title;
				const newSubTitle = info.track?.name;
				const newHeader = info.track?.header;

				if (newTitle && page === "videos") subheader.setTitle(capitalize(newTitle));

				if (newSubTitle) subheader.setSubTitle(capitalize(newSubTitle));

				dispatch(setHeaderImg(newHeader || "/media/bg/bg-13.PNG"));

				dispatch(setLogo(corporativeLogo));

				setNotFoundMessage("");
			} else {
				!videoId && setNotFoundMessage("No Video Id provided");
				setSelectedInsights([]);
				setInsightsOptions([]);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [info]);

	useEffect(() => {
		if (!searching && currentRefinement && !isConceptualSearch) {
			setShowSuggestion(isMediaPage);
			var sortedMatches = [];

			if (videoHits.length === 0) {
				setTimeout(() => {
					setShowSuggestion(false);
				}, 1500);
				if (nodeAppearances?.length) setNodeAppearances([]);
				if (videoTermSearch?.text) setVideoTermSearch({text: "", time: "", type: ""});
			} else {
				sortedMatches = getMergedAndSortedHits({videoHits, nodeAppearances});
			}

			if (timelineMatches !== sortedMatches) {
				if (!isNetgraphSearch && info && info.id) {
					sendDataLayer(
						{
							event: "video_search_bar",
							video_id: videoId,
							video_name: info.title,
							query: currentRefinement,
							result: videoHits.length || 0,
						},
						{context: contextSelector}
					);
				} else setIsNetgraphSearch(false);

				if (sortedMatches.length) {
					if (videoTermSearch?.time) {
						setTimeline(videoTermSearch.time);
					} else {
						const firstTimestamp = sortedMatches[0]?.start;
						setTimeline(firstTimestamp);
					}
				}

				setTimeLineFeature([]);
				setTimeLineMatches(sortedMatches || []);
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searching, videoHits, currentRefinement, isConceptualSearch]);

	useEffect(() => {
		if (!currentRefinement) {
			resetTimeline();
			setNodeAppearances(null);
			setShowSuggestion(false);
		}
		setClickedText(null);
	}, [currentRefinement]);

	useEffect(() => {
		const hasInsights = videoNetgraphInsights?.[0]?.name;
		if (hasInsights) {
			const allAvailableInsights = getFeaturesRecursively(videoNetgraphInsights, true);

			setInsightsOptions(allAvailableInsights);

			setSelectedInsights([]);

			const selectedInsightWithPreference = [getDefaultInsightByPreference(allAvailableInsights)];

			setSelectedInsights(selectedInsightWithPreference);
		}
	}, [
		setSelectedInsights,
		setInsightsOptions,
		getFeaturesRecursively,
		getDefaultInsightByPreference,
		videoNetgraphInsights,
	]);

	const stackPlayerFunc = f => {
		if (lastPromise) {
			lastPromise
				.then(() => {
					setLastPromise(f());
				})
				.catch(() => {
					setLastPromise(f());
				});
		} else {
			setLastPromise(f());
		}
	};
	useEffect(() => {
		if (playerCurrentTime !== null) {
			const videoTag = document.getElementById(playerId);
			if (videoTag && videoTag?.currentTime !== playerCurrentTime) {
				const setTimeFunc = async () => {
					await videoTag.pause();
					videoTag.currentTime = playerCurrentTime;
					return await videoTag.play();
				};
				stackPlayerFunc(setTimeFunc);
			}
		}
	}, [playerCurrentTime]);

	useEffect(() => {
		if (isConceptualSearch && nodeAppearances) {
			resetTimeline();

			const hasNodeAppearances = nodeAppearances?.[0]?.start || nodeAppearances?.[0]?.startTime;

			if (hasNodeAppearances) {
				const firstStart = nodeAppearances[0].start || nodeAppearances[0].startTime;
				setTimeline(firstStart);
				setTimeLineFeature(nodeAppearances);
			}

			setShowSuggestion(false);
		}
	}, [isConceptualSearch, nodeAppearances]);

	useEffect(() => {
		if (selectedInsights && selectedInsights[0] && selectedInsights[0].key) {
			sendDataLayer(
				{
					event: "video_netgraph_filter",
					video_id: videoId,
					video_name: info.title,
					selection_insight: JSON.stringify({from: oldInsights.key, to: selectedInsights[0].key}),
				},
				{context: contextSelector}
			);
		}
		setOldInsights(selectedInsights[0]);
	}, [selectedInsights, contextSelector]);

	if (notFoundMessage && info.length && !netGraphData?.length)
		return (
			<div className="col-12 d-flex justify-content-center align-items-center py-10">
				<div className="alert alert-light text-dark w-fit-content">
					<WarningRoundedIcon color="inherit" className="mr-5" />
					{notFoundMessage}
				</div>
			</div>
		);
	else if (errorMessage && (!info || !netGraphData?.length))
		return (
			<div className="col-12 d-flex justify-content-center align-items-center py-10">
				<div className="alert alert-light text-dark w-fit-content">
					<WarningRoundedIcon color="inherit" className="mr-5" />
					{errorMessage}
				</div>
			</div>
		);
	else {
		return (
			<>
				<div className={`${isMediaPage ? "mediaItemPage_container container" : isReviewPage ? "col-12" : ""}`}>
					<MediaViewer
						page={page}
						playerId={playerId}
						videoIndexerId={info?.video_indexer_id || null}
						companyId={info?.company_id || null}
						videoStarted={videoIsReady}
						insightsOptions={insightsOptions}
						selectedInsights={selectedInsights}
						setSelectedInsights={setSelectedInsights}
						prepareGetAppearancesData={onNetgraphNodeClick}
						timelineHighlight={timelineHighlight}
						showSuggestion={showSuggestion}
						timelineMatches={timelineMatches}
						timeLineFeature={timeLineFeature}
						handleMatchClick={handleMatchClick}
						setShowSuggestion={setShowSuggestion}
						setNodeAppearances={setNodeAppearances}
						resetTimeline={resetTimeline}
						currentRefinement={currentRefinement}
						refine={exactRefine}
						hits={videoHits}
						searching={searching}
						clickedText={clickedText}
					/>
					{isReviewPage ? (
						<BadgesBoard
							page={page}
							videoId={videoId}
							insightsOptions={insightsOptions}
							selectedInsights={selectedInsights}
							setSelectedInsights={setSelectedInsights}
						/>
					) : null}
					{isMediaPage ? <CarouselVideoPage /> : null}
				</div>
			</>
		);
	}
});

MediaDashboardConnectedToSearchBox.propTypes = {
	match: PropTypes.object,
	page: PropTypes.string,
};
const MediaDashboard = connectSearchBox(props => <MediaDashboardConnectedToSearchBox {...props} />);

export default MediaDashboard;
